import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Typography,
  Divider,
  useTheme,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  IconButton,
  Pagination,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MoneyIcon from '@mui/icons-material/Money';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAccounts,
  fetchAccountSummary,
  setSearchTerm,
  setStatusFilter,
  setShowClosed,
  setSortField,
  setSortDirection,
  setViewMode,
  SortField,
  SortDirection,
  ViewMode,
} from '../../redux/slices/accountsSlice';
import { Account, SyncResult } from '../../types';
import { formatCurrency } from '../../utils/helpers';
import PageHeader from '../../components/common/PageHeader';
import LoadingScreen from '../../components/common/LoadingScreen';
import ErrorMessage from '../../components/common/ErrorMessage';
import AccountCard from './components/AccountCard';
import AccountList from './components/AccountList';
import AccountDetail from './components/AccountDetail';
import AddAccountForm from './components/AddAccountForm';
import { apiService } from '../../services/api';

const CreditControl: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    filteredAccounts,
    accounts,
    summary,
    searchTerm,
    statusFilter,
    showClosed,
    sortField,
    sortDirection,
    viewMode,
    isLoading,
    error,
  } = useAppSelector((state) => state.accounts);
  const [selectedAccountContactID, setSelectedAccountContactID] = useState<string | null>(null);
  
  // Get the current selected account from Redux state (this will update when account is modified)
  const selectedAccount = selectedAccountContactID 
    ? accounts.find(acc => acc.contactID === selectedAccountContactID) || null
    : null;
  const [isAddAccountFormOpen, setIsAddAccountFormOpen] = useState(false);
  
  // Email template status state
  const [emailTemplateStatus, setEmailTemplateStatus] = useState<{
    hasTemplate: boolean;
    templateName?: string;
    loading: boolean;
  }>({
    hasTemplate: false,
    loading: true,
  });

  // Manual sync state
  const [syncState, setSyncState] = useState<{
    isLoading: boolean;
    lastResult: SyncResult | null;
    showResult: boolean;
  }>({
    isLoading: false,
    lastResult: null,
    showResult: false,
  });

  // Pagination state for card view
  const [cardViewPage, setCardViewPage] = useState(1);
  const [cardsPerPage] = useState(12); // 12 cards per page for card view

  useEffect(() => {
    dispatch(fetchAccounts());
    dispatch(fetchAccountSummary());
  }, [dispatch]);

  // Check email template status for Credit Control Agreement Request
  useEffect(() => {
    const checkEmailTemplateStatus = async () => {
      try {
        setEmailTemplateStatus(prev => ({ ...prev, loading: true }));
        const status = await apiService.getEmailTemplateTagStatus('Credit Control Agreement Request');
        setEmailTemplateStatus({
          hasTemplate: status.hasTemplate,
          templateName: status.templateName,
          loading: false,
        });
      } catch (error) {
        console.error('Error checking email template status:', error);
        setEmailTemplateStatus({
          hasTemplate: false,
          loading: false,
        });
      }
    };

    checkEmailTemplateStatus();
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    dispatch(setStatusFilter(event.target.value || null));
  };

  const handleShowClosedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setShowClosed(event.target.checked));
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    const [field, direction] = value.split('-') as [SortField, SortDirection];
    dispatch(setSortField(field));
    dispatch(setSortDirection(direction));
  };

  const handleViewModeChange = (newViewMode: ViewMode) => {
    dispatch(setViewMode(newViewMode));
    // Reset pagination when switching views
    setCardViewPage(1);
  };

  const handleCardViewPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCardViewPage(value);
  };

  const handleAccountClick = (account: Account) => {
    // Refresh the accounts list before showing the account details
    dispatch(fetchAccounts());
    dispatch(fetchAccountSummary());
    
    // Then set the selected account by contactID
    setSelectedAccountContactID(account.contactID);
  };

  const handleBackToAccounts = () => {
    setSelectedAccountContactID(null);
  };

  // Manual sync handler
  const handleManualSync = async () => {
    setSyncState(prev => ({ ...prev, isLoading: true, showResult: false }));
    
    try {
      const result = await apiService.manualSync();
      setSyncState({
        isLoading: false,
        lastResult: result,
        showResult: true,
      });
      
      // Refresh data after successful sync
      if (result.success) {
        dispatch(fetchAccounts());
        dispatch(fetchAccountSummary());
      }
      
      // Auto-hide result after 10 seconds
      setTimeout(() => {
        setSyncState(prev => ({ ...prev, showResult: false }));
      }, 10000);
      
    } catch (error) {
      console.error('Manual sync failed:', error);
      setSyncState({
        isLoading: false,
        lastResult: {
          success: false,
          timestamp: new Date().toISOString(),
          duration: '0',
          errorMessage: 'Failed to perform manual sync',
          stats: {
            recordsProcessed: 0,
            newRecords: 0,
            updatedRecords: 0,
            accountsUpdated: 0,
            statusChanges: 0,
          },
        },
        showResult: true,
      });
      
      // Auto-hide error after 10 seconds
      setTimeout(() => {
        setSyncState(prev => ({ ...prev, showResult: false }));
      }, 10000);
    }
  };

  // Summary card data
  const summaryCards = [
    {
      title: 'Active Accounts',
      value: summary?.totalActiveAccounts || 0,
      icon: <AccountBalanceIcon fontSize="large" color="primary" />,
      color: theme.palette.primary.main,
    },
    {
      title: 'Total Outstanding',
      value: formatCurrency(summary?.totalDue || 0),
      icon: <MoneyIcon fontSize="large" color="error" />,
      color: theme.palette.error.main,
    },
    {
      title: 'Total Credit',
      value: formatCurrency(summary?.totalCredit || 0),
      icon: <CreditCardIcon fontSize="large" color="success" />,
      color: theme.palette.success.main,
    },
    {
      title: 'Total Pending',
      value: formatCurrency(summary?.totalPending || 0),
      icon: <ReceiptIcon fontSize="large" color="warning" />,
      color: theme.palette.warning.main,
    },
  ];

  if (isLoading && !filteredAccounts.length) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Error Loading Accounts"
        message={error}
        onRetry={() => {
          dispatch(fetchAccounts());
          dispatch(fetchAccountSummary());
        }}
        fullPage
      />
    );
  }

  return (
    <Box>
      {selectedAccount ? (
        <AccountDetail account={selectedAccount} onClose={handleBackToAccounts} />
      ) : (
        <>
          <PageHeader
            title="Credit Control"
            subtitle="Manage accounts, credit limits, and invoices"
          />

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            {summaryCards.map((card, index) => (
              <Box key={index} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
                <Card
                  sx={{
                    height: '100%',
                    borderTop: `4px solid ${card.color}`,
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        color="text.secondary"
                      >
                        {card.title}
                      </Typography>
                      {card.icon}
                    </Box>
                    <Typography variant="h4" component="p">
                      {card.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter || ''}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Overdue">Overdue</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Show Closed Toggle, View Toggle, and Add Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showClosed}
                  onChange={handleShowClosedChange}
                  color="primary"
                />
              }
              label="Show Closed Accounts"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* View Toggle Buttons */}
              <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleViewModeChange('card')}
                  sx={{
                    borderRadius: '4px 0 0 4px',
                    backgroundColor: viewMode === 'card' ? 'primary.main' : 'transparent',
                    color: viewMode === 'card' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: viewMode === 'card' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                  title="Card View"
                >
                  <GridViewIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleViewModeChange('list')}
                  sx={{
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: viewMode === 'list' ? 'primary.main' : 'transparent',
                    color: viewMode === 'list' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: viewMode === 'list' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                  title="List View"
                >
                  <ViewListIcon fontSize="small" />
                </IconButton>
              </Box>
              {/* Email Template Status Indicator */}
              {!emailTemplateStatus.loading && (
                <Alert 
                  severity={emailTemplateStatus.hasTemplate ? "success" : "error"}
                  variant="outlined"
                  sx={{ 
                    py: 0.5,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    '& .MuiAlert-message': {
                      py: 0,
                      display: 'flex',
                      alignItems: 'center'
                    },
                    '& .MuiAlert-icon': {
                      py: 0,
                      alignItems: 'center'
                    }
                  }}
                >
                  {emailTemplateStatus.hasTemplate 
                    ? `Email Template Available${emailTemplateStatus.templateName ? ` (${emailTemplateStatus.templateName})` : ''}`
                    : "No Email Template - Email won't be sent"
                  }
                </Alert>
              )}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SyncIcon />}
                onClick={handleManualSync}
                disabled={syncState.isLoading}
                sx={{ mr: 2 }}
              >
                {syncState.isLoading ? 'Syncing...' : 'Manual Sync'}
              </Button>
              <FormControl sx={{ minWidth: 200, mr: 2 }}>
                <InputLabel id="sort-filter-label">Sort By</InputLabel>
                <Select
                  labelId="sort-filter-label"
                  id="sort-filter"
                  value={`${sortField}-${sortDirection}`}
                  label="Sort By"
                  onChange={handleSortChange}
                >
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                  <MenuItem value="balance-asc">Balance (Low to High)</MenuItem>
                  <MenuItem value="balance-desc">Balance (High to Low)</MenuItem>
                  <MenuItem value="creditLimit-asc">Credit Limit (Low to High)</MenuItem>
                  <MenuItem value="creditLimit-desc">Credit Limit (High to Low)</MenuItem>
                  <MenuItem value="availableCredit-asc">Available Credit (Low to High)</MenuItem>
                  <MenuItem value="availableCredit-desc">Available Credit (High to Low)</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsAddAccountFormOpen(true)}
              >
                Add Account
              </Button>
            </Box>
          </Box>

          {/* Sync Result Display */}
          {syncState.showResult && syncState.lastResult && (
            <Box sx={{ mb: 3 }}>
              <Alert 
                severity={syncState.lastResult.success ? "success" : "error"}
                variant="filled"
                onClose={() => setSyncState(prev => ({ ...prev, showResult: false }))}
                sx={{ 
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {syncState.lastResult.success ? 'Sync Completed Successfully' : 'Sync Failed'}
                </Typography>
                {syncState.lastResult.success ? (
                  <Box>
                    <Typography variant="body2">
                      Duration: {Math.round(parseFloat(syncState.lastResult.duration) * 1000)}ms
                    </Typography>
                    <Typography variant="body2">
                      Records Processed: {syncState.lastResult.stats.recordsProcessed}
                    </Typography>
                    <Typography variant="body2">
                      Accounts Updated: {syncState.lastResult.stats.accountsUpdated}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Data has been refreshed automatically
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2">
                    Error: {syncState.lastResult.errorMessage}
                  </Typography>
                )}
              </Alert>
            </Box>
          )}

          {/* Add Account Form Dialog */}
          <AddAccountForm
            open={isAddAccountFormOpen}
            onClose={() => setIsAddAccountFormOpen(false)}
          />

          {/* Account Display - Card or List View */}
          {viewMode === 'card' ? (
            filteredAccounts.length > 0 ? (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {filteredAccounts
                    .slice((cardViewPage - 1) * cardsPerPage, cardViewPage * cardsPerPage)
                    .map((account) => (
                      <Box key={account.contactID} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' } }}>
                        <AccountCard
                          account={account}
                          onClick={handleAccountClick}
                        />
                      </Box>
                    ))}
                </Box>
                {/* Card View Pagination */}
                {Math.ceil(filteredAccounts.length / cardsPerPage) > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Showing {((cardViewPage - 1) * cardsPerPage) + 1}-{Math.min(cardViewPage * cardsPerPage, filteredAccounts.length)} of {filteredAccounts.length} accounts
                        </Typography>
                        <Pagination
                          count={Math.ceil(filteredAccounts.length / cardsPerPage)}
                          page={cardViewPage}
                          onChange={handleCardViewPageChange}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </Paper>
                  </Box>
                )}
              </>
            ) : (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No accounts found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search or filters
                </Typography>
              </Card>
            )
          ) : (
            <AccountList
              accounts={filteredAccounts}
              onAccountClick={handleAccountClick}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CreditControl;
